import React, { useEffect, useState } from 'react';

import { Alert, Button, Card, Form, Input, List, Spin, Typography } from 'antd';
import { passwordFormRules } from 'services';

import {
  KlassClientObserver,
  addClientToKlass,
  fetchKlassClients,
  removeClientFromKlass,
} from './api';

type Props = { klassId: number };

const requestErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

/** Admin management of CLIENT observer accounts: external accounts that see
 * ONLY the klass Activity Dashboard. Several observers may watch one klass;
 * removing one detaches it from this klass without deleting the account. */
const ClientObserversCard: React.FC<Props> = ({ klassId }) => {
  const [form] = Form.useForm();
  const [clients, setClients] = useState<KlassClientObserver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [busyClientId, setBusyClientId] = useState<number>();
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(undefined);

    fetchKlassClients(klassId)
      .then(result => {
        if (active) setClients(result);
      })
      .catch(requestError => {
        if (active)
          setError(
            requestErrorMessage(
              requestError,
              'The client observers could not be loaded.',
            ),
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [klassId]);

  const removeClient = async (client: KlassClientObserver) => {
    setBusyClientId(client.id);
    setError(undefined);
    try {
      await removeClientFromKlass(klassId, client.id);
      setClients(current => current.filter(item => item.id !== client.id));
    } catch (requestError) {
      setError(
        requestErrorMessage(
          requestError,
          'The client observer could not be removed.',
        ),
      );
    } finally {
      setBusyClientId(undefined);
    }
  };

  return (
    <Card title="Client observers (Activity Dashboard)" style={{ marginTop: 16 }}>
      <Typography.Paragraph type="secondary">
        Client accounts see only this class's activity dashboard — never the
        trainer views.
      </Typography.Paragraph>
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}
      {loading ? (
        <Spin aria-label="Loading client observers" />
      ) : (
        <List
          size="small"
          dataSource={clients}
          locale={{ emptyText: 'No client observers yet.' }}
          renderItem={client => (
            <List.Item
              actions={[
                <Button
                  key="remove"
                  type="link"
                  loading={busyClientId === client.id}
                  onClick={() => removeClient(client)}
                >
                  Remove
                </Button>,
              ]}
            >
              {client.username}
            </List.Item>
          )}
        />
      )}
      <Form
        form={form}
        layout="inline"
        autoComplete="off"
        style={{ marginTop: 16 }}
        onFinish={async values => {
          setAdding(true);
          setError(undefined);
          try {
            const result = await addClientToKlass(
              klassId,
              values.username,
              values.password || undefined,
            );
            setClients(current =>
              current.some(item => item.id === result.client.id)
                ? current
                : [...current, result.client],
            );
            form.resetFields();
          } catch (requestError) {
            setError(
              requestErrorMessage(
                requestError,
                'The client observer could not be added.',
              ),
            );
          } finally {
            setAdding(false);
          }
        }}
      >
        <Form.Item name="username" rules={[{ required: true }]}>
          <Input placeholder="Username" autoComplete="off" />
        </Form.Item>
        <Form.Item
          // Password is optional here (attaching an existing client account needs
          // none), so drop passwordFormRules' `required` and keep only the policy
          // validator — the backend still enforces it for new accounts.
          name="password"
          rules={passwordFormRules.filter(rule => !('required' in rule))}
          tooltip="Required only when creating a new account; leave empty to attach an existing client."
        >
          <Input.Password
            placeholder="Password (new accounts)"
            autoComplete="new-password"
          />
        </Form.Item>
        <Button htmlType="submit" loading={adding}>
          Add observer
        </Button>
      </Form>
    </Card>
  );
};

export default ClientObserversCard;
